import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { MIAMI_IMAGE } from "../assets/images";
import { CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MiamiCongress() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", message: "", billing_nif: "", billing_address: "", billing_company_nif: "", company: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/events/congresso-miami-2026/rsvp`, { ...form, language: lang });
      const rsvp = r.data;
      try {
        const info = await axios.get(`${API}/events/congresso-miami-2026/info`);
        if (info.data.price_eur > 0) {
          const checkout = await axios.post(`${API}/payments/checkout/${rsvp.id}`, { origin_url: window.location.origin });
          if (checkout.data.url) {
            window.location.href = checkout.data.url;
            return;
          }
        }
      } catch (paymentErr) { /* fall through to success */ }
      setSubmitted(true);
      toast.success(t.common.thankYou);
    } catch (err) {
      toast.error("Erro");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="miami-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={MIAMI_IMAGE} alt="Miami" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[hsl(var(--primary))]/82" />
        </div>
        <div className="relative container-page pt-24 pb-24 sm:pt-32 sm:pb-32 text-white">
          <div className="max-w-3xl">
            <div className="overline text-white/85">{t.miami.overline}</div>
            <h1 className="display-heading text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[1.05]">
              {t.miami.title}
            </h1>
            <div className="mt-8 flex flex-wrap gap-8 text-sm">
              <div>
                <div className="overline text-[hsl(var(--accent))]">📍</div>
                <div className="mt-2 text-lg">{t.miami.where}</div>
              </div>
              <div>
                <div className="overline text-[hsl(var(--accent))]">🗓</div>
                <div className="mt-2 text-lg">{t.miami.when}</div>
              </div>
            </div>
            <p className="mt-10 text-lg text-white/85 max-w-2xl leading-relaxed">{t.miami.lead}</p>
          </div>
        </div>
      </section>

      <section className="container-page py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-6">
          <div className="overline">Portugal</div>
          <h2 className="display-heading text-3xl sm:text-4xl mt-4">
            {t.miami.portugalDelegation}
          </h2>
          <span className="accent-rule mt-8 block" />
        </div>
        <div className="lg:col-span-6">
          <div className="bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] p-8 sm:p-10">
            <div className="overline">{t.miami.interestTitle}</div>
            <p className="mt-3 text-[hsl(var(--muted-foreground))] text-sm">{t.miami.interestBody}</p>

            {submitted ? (
              <div className="mt-8 flex items-start gap-3 text-[hsl(var(--primary))]" data-testid="miami-success">
                <CheckCircle2 className="h-5 w-5 mt-1" />
                <div>
                  <div className="font-medium">{t.presentation.successTitle}</div>
                  <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                    {t.presentation.successBody}
                  </div>
                </div>
              </div>
            ) : (
              <form className="mt-6 space-y-4" onSubmit={submit} data-testid="miami-interest-form">
                <Input
                  required
                  testid="miami-name"
                  label={t.presentation.fields.fullName}
                  value={form.full_name}
                  onChange={(v) => setForm({ ...form, full_name: v })}
                />
                <Input
                  required
                  type="email"
                  testid="miami-email"
                  label={t.presentation.fields.email}
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                />
                <Input
                  testid="miami-phone"
                  label={t.presentation.fields.phone + " " + t.common.optional}
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                />
                <Input
                  testid="miami-company"
                  label={t.presentation.fields.company + " " + t.common.optional}
                  value={form.company}
                  onChange={(v) => setForm({ ...form, company: v })}
                />
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-2">
                    {t.presentation.fields.message}
                  </label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={3}
                    className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 focus:outline-none focus:border-[hsl(var(--accent))]"
                    data-testid="miami-message"
                  />
                </div>

                <details className="border-t border-[hsl(var(--border))] pt-5 group" data-testid="miami-billing-section">
                  <summary className="cursor-pointer list-none flex items-center justify-between text-sm text-[hsl(var(--primary))] font-medium">
                    <span>Dados para Declaração de Donativo <span className="text-[hsl(var(--muted-foreground))]">(opcional, para contabilidade)</span></span>
                    <span className="text-xs transition-transform group-open:rotate-180">▼</span>
                  </summary>
                  <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
                    A Associação CIEES Portugal não emite faturas. Se precisar de uma <strong>Declaração de Donativo</strong> para efeitos contabilísticos, preencha estes dados. Receberá um PDF em anexo após o pagamento.
                  </p>
                  <div className="mt-4 grid sm:grid-cols-2 gap-4">
                    <Input testid="miami-billing-nif" label="NIF pessoal" value={form.billing_nif} onChange={(v) => setForm({ ...form, billing_nif: v })} />
                    <Input testid="miami-billing-company-nif" label="NIF da empresa (se aplicável)" value={form.billing_company_nif} onChange={(v) => setForm({ ...form, billing_company_nif: v })} />
                  </div>
                  <div className="mt-4">
                    <Input testid="miami-billing-address" label="Morada fiscal" value={form.billing_address} onChange={(v) => setForm({ ...form, billing_address: v })} />
                  </div>
                </details>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary disabled:opacity-60"
                  data-testid="miami-submit-button"
                >
                  {loading ? t.common.sending : t.common.send}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-2">
        {label}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 focus:outline-none focus:border-[hsl(var(--accent))]"
        data-testid={testid}
      />
    </div>
  );
}
